import { useState } from 'react';
import EPageHeader from '@/components/everridge/EPageHeader';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { useTranslation } from 'react-i18next';
import { FilterHelper } from '@/utils/filterHelper';
import EFilterButton from '@/components/everridge/EFilterButton';
import ESearchInput from '@/components/everridge/ESearchInput';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { useUserActivitiesFilter } from '@/features/superAdmin/features/userActivities/hooks/useUserActivitiesFilter';
import { useUserActivitiesListApi } from '@/features/superAdmin/features/userActivities/hooks/useUserActivitiesListApi';
import useUserActivitiesPageColumns from '@/features/superAdmin/features/userActivities/components/UserActivitiesPageColumns';
import UserActivitiesPageFilter from '@/features/superAdmin/features/userActivities/components/UserActivitiesPageFilter';
import UserActivityDetailsDialog from '@/features/superAdmin/features/userActivities/components/UserActivityDetailsDialog';
import { UserActivityListItemDto } from '@/features/superAdmin/features/userActivities/types/superAdmin-userActivities-dto';

const SuperAdminUserActivitiesPage = () => {
    const [showFilter, setShowFilter] = useState(false);
    const [page, setPage] = useState(0);
    const [selectedActivity, setSelectedActivity] = useState<UserActivityListItemDto | null>(null);

    const { t } = useTranslation();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const userRole = useUserRole();
    const columns = useUserActivitiesPageColumns();

    const { filter, setFilter } = useUserActivitiesFilter();
    const { userActivitiesListData, isLoadingUserActivitiesListData } = useUserActivitiesListApi();

    if (userRole !== Roles.superAdmin) return null;

    const filtersQuantity = FilterHelper.countFilterValues(filter, [
        'take',
        'skip',
        'orderBy',
        'orderDirection',
        'searchText',
        'startDate',
        'endDate',
    ]) + (filter.source !== null ? 1 : 0) + (filter.userId ? 1 : 0) + (filter.tenantId ? 1 : 0);

    const handleSearchText = (text: string) => {
        setPage(0);
        setFilter({ searchText: text, skip: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setFilter({
            skip: filter.take * newPage,
        });
    };

    return (
        <section className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader
                title={t('main.superAdmin.userActivities.title')}
                search={
                    <ESearchInput
                        value={filter.searchText}
                        setValue={handleSearchText}
                        placeholder={t('main.superAdmin.userActivities.form.search.placeholder')}
                        containerClassName={cn(!isMobile && 'w-60')}
                    />
                }
            >
                <EFilterButton
                    name={t('elements.buttons.filter')}
                    filtersQuantity={filtersQuantity}
                    onClick={() => setShowFilter(true)}
                    isMobile={isMobile}
                />
            </EPageHeader>
            <ETableWithApiPagination
                columns={columns}
                data={userActivitiesListData}
                paginationStep={filter.take}
                page={page}
                setPage={handlePageChange}
                isLoadingPage={isLoadingUserActivitiesListData}
                onRowClick={setSelectedActivity}
            />
            {selectedActivity && (
                <UserActivityDetailsDialog
                    activity={selectedActivity}
                    onClose={() => setSelectedActivity(null)}
                    isMobile={isMobile}
                />
            )}
            {showFilter && (
                <UserActivitiesPageFilter onClose={() => setShowFilter(false)} isMobile={isMobile} />
            )}
        </section>
    );
};

export default SuperAdminUserActivitiesPage;
